import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { Menu as MenuIcon } from '@mui/icons-material';
import { Box, IconButton, Link as MuiLink, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useSubscription } from '../../../contexes/SubscriptionProvider';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import { useAnalytics } from '../../../hooks/useAnalytics';
import useAuth from '../../../hooks/useAuth';
import { shouldShowUpgradeCta } from '../../../helpers/billingPlanSwitchUi';
import { canPerformAction, PERMISSIONS } from '../../../utils/permissions';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { useTranslation } from '../../../hooks/useTranslation';
import { privateNavPageTitleKey } from '../../../helpers/privateNavPageTitle';
import StyledButton from '../Button/StyledButton';
import StyledLogInButton from '../Button/StyledLogInButton';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import Logo from '../Logo/Logo';
import StyledNavBarLogoSection from './StyledNavBarLogoSection';
import StyledNavLinks from './StyledNavLinks';
import UsageQuotaTopBar from './UsageQuotaTopBar';
import UserAvatarMenu from './UserAvatarMenu';

interface NavBarProps {
  onSidebarToggle?: () => void;
}

const NavBar = ({ onSidebarToggle }: NavBarProps): JSX.Element => {
  const { currentUser, login } = useAuth();
  const { subscription, company } = useSubscription();
  const { track } = useAnalytics();
  const showSnackBar = useSnackBar();
  const { t } = useTranslation();
  const location = useLocation();
  const showUpgradeCta = shouldShowUpgradeCta(company, subscription);
  const canManageBilling = Boolean(
    currentUser && canPerformAction(currentUser, PERMISSIONS.MANAGE_BILLING)
  );

  useEffect(() => {
    if (subscription) {
      // Get the current subscription plan amount
      const subscriptionPlanAmount = localStorage.getItem(
        'subscription.plan_amount'
      );

      // User is still using the free plan
      if (!subscriptionPlanAmount) {
        localStorage.setItem('subscription.plan_amount', String(0));
      }

      // Only proceed if plan_amount is a valid number
      if (typeof subscription.plan_amount === 'number') {
        const currentPlanAmount = subscription.plan_amount.toString();

        // Check if this is a new/different plan amount
        if (subscriptionPlanAmount !== currentPlanAmount) {
          showSnackBar(
            t(
              'Congrats on picking your new plan - we are glad to have you with us!'
            ),
            'success'
          );
          localStorage.setItem('subscription.plan_amount', currentPlanAmount);
        }
      }
    }
  }, [subscription]);

  const mobilePrivateNav = Boolean(currentUser && onSidebarToggle);
  const pageTitleKey = privateNavPageTitleKey(location.pathname);
  const showBackToDashboard = location.pathname !== '/dashboard';

  if (mobilePrivateNav) {
    return (
      <Box
        sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        <Box sx={{ px: 0.25 }}>
          {showBackToDashboard && (
            <MuiLink
              component={Link}
              to="/dashboard"
              underline="none"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.1,
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: '#94A3B8',
                mb: 0.25,
                '&:hover': { color: '#64748B' },
              }}
            >
              <ChevronLeft sx={{ fontSize: '1.05rem', ml: -0.5 }} aria-hidden />
              {t('Back to dashboard')}
            </MuiLink>
          )}
          <Typography
            component="h1"
            sx={{
              m: 0,
              fontSize: '1.25rem',
              fontWeight: 700,
              color: '#0F172A',
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}
          >
            {t(pageTitleKey)}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <StyledNavBarLogoSection>
            <IconButton
              onClick={onSidebarToggle}
              edge="start"
              color="inherit"
              aria-label={t('Toggle sidebar')}
              sx={{ mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          </StyledNavBarLogoSection>
          <StyledNavLinks>
            {canManageBilling && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  minWidth: 0,
                  px: 1.25,
                  py: 0.65,
                  borderRadius: 999,
                  bgcolor: '#FFFFFF',
                  border: '1px solid rgba(15, 23, 42, 0.06)',
                  boxShadow: '0px 4px 14px rgba(15, 23, 42, 0.08)',
                }}
              >
                <UsageQuotaTopBar />
              </Box>
            )}
            {showUpgradeCta && canManageBilling && (
              <Link
                to={`/pricing`}
                onClick={() => {
                  track(AnalyticsEvents.UPGRADE_BUTTON_CLICKED);
                }}
              >
                <StyledButton variant="contained" color="success" size="small">
                  {t('Upgrade')}
                </StyledButton>
              </Link>
            )}
            <UserAvatarMenu />
          </StyledNavLinks>
        </Box>
      </Box>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <StyledNavBarLogoSection>
        {/* Logo for unauthenticated users */}
        {!currentUser && (
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Logo />
          </Link>
        )}

        {/* Sidebar toggle button for authenticated users */}
        {currentUser && onSidebarToggle && (
          <IconButton
            onClick={onSidebarToggle}
            edge="start"
            color="inherit"
            aria-label={t('Toggle sidebar')}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </StyledNavBarLogoSection>

      {/* Right side actions */}
      <StyledNavLinks>
        {currentUser ? (
          <>
            {canManageBilling && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  minWidth: 0,
                }}
              >
                <UsageQuotaTopBar />
              </Box>
            )}
            {showUpgradeCta && canManageBilling && (
              <Link
                to={`/pricing`}
                onClick={() => {
                  track(AnalyticsEvents.UPGRADE_BUTTON_CLICKED);
                }}
              >
                <StyledButton variant="contained" color="success" size="small">
                  {t('Upgrade')}
                </StyledButton>
              </Link>
            )}
          </>
        ) : (
          <>
            <LanguageSelector />
            <StyledLogInButton
              onClick={() => {
                track(AnalyticsEvents.HOME_PAGE_SIGN_IN_BUTTON_CLICKED);
                track(AnalyticsEvents.SIGN_IN_STARTED);
                login();
              }}
              variant="outlined"
              color="inherit"
              size="small"
            >
              {t('Sign In')}
            </StyledLogInButton>
          </>
        )}
      </StyledNavLinks>
    </div>
  );
};

export default NavBar;
